<template>
  <div class="wrap">
    <div class="card" role="main" aria-labelledby="registerTitle">
      <div class="logo-row">
        <div class="logo-badge">L</div>
        <div>
          <h1 id="registerTitle" class="title">商户注册</h1>
          <p class="subtitle">先完成账号注册，再同时绑定直播域名和商城域名。</p>
        </div>
      </div>

      <div class="steps">
        <div class="step" :class="{ active: !registerSuccess, done: registerSuccess }">
          <span class="step-index">1</span>
          <span>注册账号</span>
        </div>
        <div class="step-line"></div>
        <div class="step" :class="{ active: registerSuccess && !domainSaved, done: domainSaved }">
          <span class="step-index">2</span>
          <span>绑定域名</span>
        </div>
        <div class="step-line"></div>
        <div class="step" :class="{ active: domainSaved && !domainVerified, done: domainVerified }">
          <span class="step-index">3</span>
          <span>验证解析</span>
        </div>
      </div>

      <div v-if="!registerSuccess">
        <form @submit.prevent="onSubmit" autocomplete="on" novalidate>
          <div class="field">
            <label for="username">手机号</label>
            <input id="username" v-model.trim="form.username" type="text" placeholder="请输入手机号" maxlength="30" />
            <div class="tips">该手机号将作为登录账号使用，并用于接收短信验证码。</div>
            <div v-if="errors.username" class="error">{{ errors.username }}</div>
          </div>

          <div class="field">
            <label for="commercialName">商户名称</label>
            <input id="commercialName" v-model.trim="form.commercialName" type="text" placeholder="请输入商户名称"
              maxlength="50" />
            <div v-if="errors.commercialName" class="error">{{ errors.commercialName }}</div>
          </div>

          <div class="field input-with-btn">
            <label for="password">密码</label>
            <input id="password" v-model.trim="form.password" :type="showPwd ? 'text' : 'password'"
              placeholder="请输入 6-20 位密码" maxlength="20" />
            <button type="button" class="inline-btn" @click="togglePwd">
              {{ showPwd ? '隐藏' : '显示' }}
            </button>
            <div v-if="errors.password" class="error">{{ errors.password }}</div>
          </div>

          <div class="field">
            <label for="repeatPassword">确认密码</label>
            <input id="repeatPassword" v-model.trim="form.repeatPassword" :type="showPwd ? 'text' : 'password'"
              placeholder="请再次输入密码" maxlength="20" />
            <div v-if="errors.repeatPassword" class="error">{{ errors.repeatPassword }}</div>
          </div>

          <div class="field verify-group">
            <div class="verify-input">
              <label for="mobileCode">短信验证码</label>
              <input id="mobileCode" v-model.trim="form.mobile_verify_code" type="text" placeholder="请输入验证码"
                maxlength="6" />
              <div v-if="errors.mobile_verify_code" class="error">{{ errors.mobile_verify_code }}</div>
            </div>
            <button type="button" class="ghost-btn code-btn" :disabled="countdown > 0 || codeLoading"
              @click="sendMobileCode">
              {{ countdown > 0 ? `${countdown}s 后重试` : codeLoading ? '发送中...' : '获取验证码' }}
            </button>
          </div>

          <button class="btn" type="submit" :disabled="loading">
            {{ loading ? '注册中...' : '立即注册' }}
          </button>

          <div v-if="errors.global" class="error global-error">{{ errors.global }}</div>
        </form>
      </div>

      <div v-else class="success-panel">
        <div class="success-banner">
          <div class="success-icon">✓</div>
          <div>
            <h2>注册成功</h2>
            <p>请继续绑定域名/开启直播</p>
          </div>
        </div>
        <button class="btn">
          <router-link to='/' style="color: #fff;">
            去直播
          </router-link>
        </button>
        <DomainSettingsPanel ref="registerDomainPanel" mode="register" :auto-load="true" :show-title="false"
          @bound="handleDomainBound" @verified="handleDomainVerified" @state-change="handleDomainState" />
      </div>
    </div>
  </div>
</template>

<script>
import { register, sendCode } from '@/api'
import DomainSettingsPanel from '@/components/DomainSettingsPanel.vue'
import { setToken } from '@/utils/auth'

export default {
  name: 'CommercialRegisterPage',
  components: {
    DomainSettingsPanel,
  },
  data() {
    return {
      showPwd: false,
      loading: false,
      codeLoading: false,
      countdown: 0,
      timer: null,
      registerSuccess: false,
      domainSaved: false,
      domainVerified: false,
      form: {
        username: '',
        commercialName: '',
        password: '',
        repeatPassword: '',
        mobile_verify_code: '',
      },
      appid: null,
      errors: {},
    }
  },
  beforeDestroy() {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
  },
  methods: {
    togglePwd() {
      this.showPwd = !this.showPwd
    },
    startCountdown() {
      this.countdown = 60
      if (this.timer) {
        clearInterval(this.timer)
      }
      this.timer = setInterval(() => {
        if (this.countdown <= 1) {
          clearInterval(this.timer)
          this.timer = null
          this.countdown = 0
          return
        }
        this.countdown -= 1
      }, 1000)
    },
    async sendMobileCode() {
      if (this.countdown > 0 || this.codeLoading) return

      this.errors = {
        ...this.errors,
        username: '',
        global: '',
      }

      if (!this.form.username) {
        this.errors.username = '请输入手机号'
        return
      }

      const mobile = String(this.form.username).trim()
      if (!/^\S+$/.test(mobile)) {
        this.errors.username = '手机号不能包含空格'
        return
      }

      this.codeLoading = true
      try {
        await sendCode({ mobile })
        this.$message.success('验证码已发送，请注意查收')
        this.startCountdown()
      } catch (error) {
        this.errors.global = error.message || '验证码发送失败，请稍后重试'
      } finally {
        this.codeLoading = false
      }
    },
    validate() {
      this.errors = {}

      if (!this.form.username) {
        this.errors.username = '请输入手机号'
      } else if (!/^\S+$/.test(this.form.username)) {
        this.errors.username = '手机号不能包含空格'
      }

      if (!this.form.commercialName) {
        this.errors.commercialName = '请输入商户名称'
      }

      if (!this.form.password) {
        this.errors.password = '请输入密码'
      } else if (this.form.password.length < 6) {
        this.errors.password = '密码长度至少 6 位'
      }

      if (!this.form.repeatPassword) {
        this.errors.repeatPassword = '请再次输入密码'
      } else if (this.form.repeatPassword !== this.form.password) {
        this.errors.repeatPassword = '两次输入的密码不一致'
      }

      if (!this.form.mobile_verify_code) {
        this.errors.mobile_verify_code = '请输入短信验证码'
      }

      return Object.keys(this.errors).length === 0
    },
    async onSubmit() {
      // this.registerSuccess = true
      //   this.$message.success('注册成功')
      //   return
      if (!this.validate()) return

      this.loading = true
      this.errors.global = ''

      try {
        const payload = {
          username: String(this.form.username).trim(),
          commercialName: this.form.commercialName,
          password: this.form.password,
          repeatPassword: this.form.repeatPassword,
          mobile_verify_code: this.form.mobile_verify_code,
        }
        const res = await register(payload)
        const token = res.data && (res.data.token || res.data.access_token)

        if (token) {
          this.$store.commit('SET_TOKEN', token)
          localStorage.setItem('lvtu_live_apitoken', token)
          setToken(token)
          this.appid = res
        }

        this.registerSuccess = true
        this.$message.success('注册成功')
      } catch (error) {
        this.errors.global = error.message || '注册失败，请稍后重试'
      } finally {
        this.loading = false
      }
    },
    handleDomainState(summary) {
      this.domainSaved = !!summary.allSaved
      this.domainVerified = !!summary.allVerified
    },
    handleDomainBound(summary) {
      this.domainSaved = !!summary.allSaved
      this.domainVerified = !!summary.allVerified
    },
    handleDomainVerified(summary) {
      this.domainSaved = !!summary.allSaved

      if (!summary.allVerified) {
        this.domainVerified = false
        return
      }

      this.domainVerified = true
      this.$notify({
        type: 'success',
        message: '直播域名和商城域名均已验证成功，正在进入设置中心',
      })

      setTimeout(() => {
        this.$router.push('/settings')
      }, 800)
    },
  },
}
</script>

<style scoped>
html,
body {
  margin: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  background: #f5f7fb;
  color: #1f2937;
}

.wrap {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 32px 16px;
  background:
    radial-gradient(circle at top left, rgba(37, 99, 235, 0.12), transparent 32%),
    linear-gradient(180deg, #f8fbff 0%, #f5f7fb 100%);
}

.card {
  width: 100%;
  max-width: 980px;
  background: #fff;
  border-radius: 20px;
  padding: 28px;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.08);
  box-sizing: border-box;
}

.logo-row {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 20px;
}

.logo-badge {
  width: 52px;
  height: 52px;
  border-radius: 16px;
  background: linear-gradient(135deg, #2563eb, #7c3aed);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 22px;
  font-weight: 700;
  box-shadow: 0 10px 24px rgba(37, 99, 235, 0.26);
}

.title {
  margin: 0;
  font-size: 28px;
  line-height: 1.2;
}

.subtitle {
  margin: 6px 0 0;
  color: #6b7280;
  font-size: 14px;
}

.steps {
  display: flex;
  align-items: center;
  margin: 24px 0 28px;
}

.step {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #94a3b8;
  font-size: 14px;
  white-space: nowrap;
}

.step.active,
.step.done {
  color: #111827;
}

.step-index {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: #e5e7eb;
  color: #6b7280;
  font-weight: 600;
}

.step.active .step-index,
.step.done .step-index {
  background: #2563eb;
  color: #fff;
}

.step-line {
  flex: 1;
  height: 1px;
  background: #e5e7eb;
  margin: 0 12px;
}

.field {
  margin-bottom: 18px;
}

label {
  display: block;
  margin-bottom: 8px;
  font-size: 13px;
  color: #374151;
  font-weight: 600;
}

input[type='text'],
input[type='password'] {
  width: 100%;
  padding: 13px 14px;
  border-radius: 12px;
  border: 1px solid #dbe3ef;
  font-size: 15px;
  background: #fff;
  outline: none;
  transition: all 0.2s ease;
  box-sizing: border-box;
}

input[type='text']:focus,
input[type='password']:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.1);
}

.tips {
  margin-top: 8px;
  color: #94a3b8;
  font-size: 12px;
  line-height: 1.5;
}

.input-with-btn {
  position: relative;
}

.inline-btn {
  position: absolute;
  right: 12px;
  top: 39px;
  border: 0;
  background: transparent;
  color: #2563eb;
  font-size: 13px;
  cursor: pointer;
}

.verify-group {
  display: grid;
  grid-template-columns: 1fr 132px;
  gap: 12px;
  align-items: end;
}

.code-btn,
.ghost-btn {
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
}

.ghost-btn {
  height: 48px;
  border: 1px solid #c7d2fe;
  color: #2563eb;
  background: #eef4ff;
}

.btn {
  width: 100%;
  border: 0;
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  color: #fff;
  padding: 14px 18px;
  border-radius: 14px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 12px 22px rgba(37, 99, 235, 0.2);
}

.btn[disabled],
.ghost-btn[disabled] {
  opacity: 0.6;
  cursor: not-allowed;
  box-shadow: none;
}

.global-error {
  margin-top: 14px;
}

.error {
  margin-top: 8px;
  color: #e11d48;
  font-size: 13px;
}

.success-panel {
  border-top: 1px solid #eef2f7;
  padding-top: 8px;
}

.success-banner {
  display: flex;
  gap: 14px;
  align-items: center;
  padding: 18px;
  border-radius: 16px;
  background: linear-gradient(180deg, #effaf4 0%, #f8fffb 100%);
  border: 1px solid #b7ebc6;
  margin-bottom: 24px;
}

.success-banner h2 {
  margin: 0 0 6px;
  font-size: 20px;
}

.success-banner p {
  margin: 0;
  color: #4b5563;
  line-height: 1.6;
}

.success-icon {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: #16a34a;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  font-weight: 700;
}

@media (max-width: 768px) {
  .card {
    padding: 20px;
    border-radius: 16px;
  }

  .title {
    font-size: 24px;
  }

  .steps {
    overflow-x: auto;
    padding-bottom: 6px;
  }

  .verify-group {
    grid-template-columns: 1fr;
  }
}
</style>
